import * as React from "react";
import moment from "moment";
import { ExpandableList, ExpandableItem } from "@conserveit/blocks";
import {
  getGroupAssets,
  getUnassociatedEquipsCount,
  getGroupId,
  getEquipsCount,
  GroupAssets,
} from "../utils";
import { getPersisted, setPersisted } from "../../../utils/storage";
import GroupGrid from "./components/GroupGrid";
import { GroupBy } from "../utils/types";
import ProgressiveList from "../../ProgressiveList";
import { useState as useAnalysisState } from "../../../context/analysis";
import { ViewMode } from "../../../context/analysis/types";
import { EXPANDING_KEY } from "../utils/constants";

interface Props {
  finalGrouped: {
    composed: Insite.Insights.ComposedInsight[][];
    group: GroupBy;
  };
  showSnoozed: boolean;
  expandAll: boolean;
  filtered: Insite.Insights.ComposedOccurrence[];
}

function getInitialAmount(groupCount: number) {
  const DEFAULT_RENDER_LIMIT = 20;
  return groupCount > DEFAULT_RENDER_LIMIT ? DEFAULT_RENDER_LIMIT : groupCount;
}

function renderGroup(
  composed: Insite.Insights.ComposedInsight[][],
  group: GroupBy,
  showSnoozed: boolean,
  expandAll: boolean,
  groupAssets: GroupAssets,
  filtered: Insite.Insights.ComposedOccurrence[],
  viewMode: ViewMode
) {
  return (index: number) => {
    const groupedOccurrences = composed[index];
    if (!groupedOccurrences) return null;
    const instance = groupedOccurrences[0];
    const postGroupedOccurrences = showSnoozed
      ? groupedOccurrences
      : groupedOccurrences.filter((ins) => !ins.isAllSnoozed);
    const insightsCount = postGroupedOccurrences.length;
    const insightsCountToday = groupedOccurrences.reduce(
      (count, ins) =>
        count + (ins.latest === moment().format("YYYY-MM-DD") ? 1 : 0),
      0
    );
    const equipsCount = getEquipsCount(groupedOccurrences, showSnoozed);
    const unassociatedEquipsCount = getUnassociatedEquipsCount(
      groupedOccurrences,
      showSnoozed
    );

    const key = getGroupId(group, instance);

    const expanded = getPersisted(
      { key: EXPANDING_KEY[viewMode], prop: key },
      false
    );
    const { Heading } = groupAssets;
    return (
      <ExpandableItem
        key={`${key}-${expandAll}-${index}`}
        expanded={expandAll || expanded}
        onClick={(visible) =>
          setPersisted(
            { key: EXPANDING_KEY[viewMode], prop: key },
            Boolean(visible)
          )
        }
        renderHeader={
          <Heading
            instance={instance}
            insightsCount={insightsCount}
            insightsCountToday={insightsCountToday}
            equipsCount={equipsCount}
            unassociatedEquipsCount={unassociatedEquipsCount}
          />
        }
      >
        <GroupGrid
          persistKey={key}
          groupAssets={groupAssets}
          occurrences={postGroupedOccurrences}
          filtered={filtered}
          showSnoozed={showSnoozed}
        />
      </ExpandableItem>
    );
  };
}

export const ExpandableGroup = React.memo<Props>(
  ({ showSnoozed, finalGrouped, expandAll, filtered }) => {
    const { viewMode } = useAnalysisState();

    const [rowCount, setRowCount] = React.useState(
      finalGrouped.composed.length
    );
    const getAssets = React.useCallback(
      () => getGroupAssets(finalGrouped.group),
      [finalGrouped.group]
    );

    React.useLayoutEffect(() => {
      if (filtered.length === 0) {
        setRowCount(finalGrouped.composed.length);
      } else {
        setRowCount(filtered.length);
      }
    }, [filtered.length, finalGrouped.composed.length]);

    return (
      <ExpandableList>
        {finalGrouped.composed.length > 0 && (
          <ProgressiveList
            initialAmount={getInitialAmount(finalGrouped.composed.length)}
            useWindowScroll
            progressiveAmount={5}
            renderItem={renderGroup(
              finalGrouped.composed,
              finalGrouped.group,
              showSnoozed,
              expandAll,
              getAssets(),
              filtered,
              viewMode
            )}
            rowCount={rowCount}
          />
        )}
      </ExpandableList>
    );
  }
);
